<script setup lang="ts">
import { AppStandardModal } from '@libs/ui';

import type { PriceRibbonMonth } from './PriceRibbon.model';

interface MonthsSelectorModalProps {
  months: PriceRibbonMonth[];
  selectedMonthIsoDate: string;
}

const props = defineProps<MonthsSelectorModalProps>();

interface MonthsSelectorModalEmits {
  (eventName: 'confirm', newMonthSelection: Date): void;
  (eventName: 'close'): void;
}

const emit = defineEmits<MonthsSelectorModalEmits>();

function onMonthSelect(month: PriceRibbonMonth): void {
  if (!month.isExtended) {
    emit('confirm', new Date(month.firstDayInMonthISO));
  }
}
</script>

<template>
  <AppStandardModal
    class="months-selector-modal"
    @close="emit('close')"
    @confirm="emit('close')"
  >
    <template #title>
      {{ $t('planner.months_selector_title') }}
    </template>
    <template #body>
      <div class="months-selector-modal-body">
        <div
          v-for="month of props.months"
          :key="month.firstDayInMonthISO"
          :class="['month', { selected: month.isExtended }]"
          @click.stop="onMonthSelect(month)"
        >
          <div class="month-description">
            <span class="name">
              {{ month.month }}
            </span>
            <span class="year">
              {{ month.year }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </AppStandardModal>
</template>

<style scoped lang="scss">
.months-selector-modal {
  --tile-height: 72px;
  .months-selector-modal-body {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: var(--spacing-8);
    .month {
      background-color: var(--color-secondary-95);
      border-radius: var(--border-radius-12);
      height: var(--tile-height);
      .month-description {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        color: var(--color-secondary-40);
        cursor: pointer;
        border-radius: var(--border-radius-12);
        transition: all var(--transition-short);
        @include on-hover {
          background-color: var(--color-secondary-93);
        }
        .name {
          font: var(--font-label-xl-bold);
        }
        .year {
          @include font-caption-l-light;
        }
      }
      &.selected {
        .month-description {
          background-color: var(--color-primary-30);
          color: var(--color-white);
          cursor: default;
          @include on-hover {
            background-color: var(--color-primary-30);
          }
        }
      }
    }
  }
}
</style>
